// Mocks only: prices of the mock trades from the market at their times, so
// the tables and the trade details chart tell the same story. The real API
// sends the prices and P&L itself
import { fetchPriceAt } from "@helpers/futures/klines";
import { formatUsd } from "../../Controls/TradeForm/utils/format";
import {
  formatMultiplier,
  getMockTradeAmounts,
  getMultiplier,
  getQuantity,
  getTradePnl,
} from "../../TradeModals/TradeDetailsModal/utils/tradeMath";
import { formatDollarPrice, formatSignedPnl } from "../positionFormat";

// Past prices don't change, one request per moment
const pastPrices = new Map();

const parseMoney = (value) => {
  const numeric = parseFloat(String(value ?? "").replace(/[^0-9.-]/g, ""));

  return Number.isFinite(numeric) ? numeric : null;
};

const getPastPrice = (symbol, time) => {
  const key = `${symbol}:${time}`;

  if (!pastPrices.has(key)) {
    pastPrices.set(
      key,
      fetchPriceAt(symbol, time).catch(() => {
        pastPrices.delete(key);
        return null;
      }),
    );
  }

  return pastPrices.get(key);
};

const getLastPrice = (symbol) =>
  fetchPriceAt(symbol, Date.now()).catch(() => null);

/**
 * Fills entry, exit (or the current price), P&L and multiplier of a mock
 * trade. A best trade (isWinner) takes the direction the market went.
 */
const withMarketPrices = async (row) => {
  const coin = row.trade?.coin ?? row.trade?.symbol;
  const symbol = `${coin}USDT`;
  const isOpen = row.closedAt == null;
  const [entryPrice, exitPrice] = await Promise.all([
    getPastPrice(symbol, row.openedAt),
    isOpen ? getLastPrice(symbol) : getPastPrice(symbol, row.closedAt),
  ]);

  if (entryPrice == null || exitPrice == null) return row;

  const direction = row.isWinner
    ? exitPrice >= entryPrice
      ? "up"
      : "down"
    : row.trade.direction;
  const { margin, wager, feeInPnl } = getMockTradeAmounts(
    parseMoney(row.wager),
    isOpen,
  );
  const quantity = getQuantity(margin, parseMoney(row.leverage), entryPrice);
  const pnl = getTradePnl(
    { isLong: direction !== "down", entryPrice, quantity, wager, feeInPnl },
    exitPrice,
  );
  const profitLoss = formatSignedPnl(pnl);
  const multiplier = formatMultiplier(getMultiplier(wager, pnl));
  const openTradeFields = {
    price: formatDollarPrice(exitPrice),
    wager: formatUsd(wager),
    value: pnl != null ? formatUsd(wager + pnl) : "—",
  };
  const closedTradeFields = { exitPrice, exit: formatDollarPrice(exitPrice) };

  return {
    ...row,
    trade: { ...row.trade, direction },
    entryPrice,
    entry: formatDollarPrice(entryPrice),
    ...(isOpen ? openTradeFields : closedTradeFields),
    // Field names of the public feed / profile trades and of the best trades
    profitLoss,
    multiplier,
    profit: profitLoss,
    payoutMultiplierIncludingFee: multiplier,
  };
};

/** @param {object[]} rows mock trades with openedAt and closedAt */
export const withMockMarketPrices = (rows) =>
  Promise.all(rows.map(withMarketPrices));
